import { useState } from "react";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";

import AlertModal from "components/AlertModal";
import ModalComponent from "components/ModalComponent";
import { useProfessional } from "providers/Professional";
import { api } from "services";
import { ContractStatus, ContractStatusDescription } from "types/enums";
import { Professional } from "types/professional";
import { cpfAndCnpjMask, mobilePhoneMask } from "utils/formMasks";
import { toast } from "utils/toast";

import ProfessionalEditForm from "../Forms/ProfessionalEditForm";

import * as C from "./styles";

interface ProfessionalCardProps {
  professional: Professional;
  companyId?: number;
  isActive?: boolean;
  showProfessionalEmbeddedContractModal: (userId: number) => void;
}

const CONTRACT_ICON: Record<number, string> = {
  [ContractStatus.None]: "uil-file-question-alt",
  [ContractStatus.Signed]: "uil-file-check-alt",
  [ContractStatus.Rejected]: "uil-file-times-alt",
  [ContractStatus.Negociating]: "uil-file-search-alt",
};

const getInitials = (name: string) => {
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const ProfessionalCard = ({
  professional,
  companyId,
  showProfessionalEmbeddedContractModal,
  isActive = false,
}: ProfessionalCardProps) => {
  const [alertModalShow, setAlertModalShow] = useState(false);
  const [professionalEditModalShow, setProfessionalEditModalShow] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    setInactiveProfessionalsCurrentPage,
    setActiveProfessionalsCurrentPage,
    getInactiveProfessionals,
    getActiveProfessionals,
  } = useProfessional();

  const handleActivateProfessional = async () => {
    setIsLoading(true);
    try {
      await api.patch(
        `/Professional/${professional.id}/${isActive ? "disable" : "enable"}`
      );
      setIsLoading(false);
      toast.fire({
        icon: "success",
        title: `Profissional ${isActive ? "des" : ""}ativado com sucesso!`,
      });
      setInactiveProfessionalsCurrentPage(1);
      setActiveProfessionalsCurrentPage(1);
      getInactiveProfessionals(companyId);
      getActiveProfessionals(companyId);
      setAlertModalShow(false);
    } catch {
      setIsLoading(false);
      toast.fire({ icon: "error", title: "Ocorreu um erro, tente novamente." });
    }
  };

  const professionalName = professional.socialName
    ? `${professional.socialName} (${professional.name})`
    : professional.name;

  const phone = mobilePhoneMask(
    `${professional.dddMobilePhone}${professional.mobilePhone}`
  );

  const address = professional.addressCEP
    ? `${professional.addressRoad}, ${professional.addressNumber}${
        professional.addressComplement
          ? ` - ${professional.addressComplement},`
          : ","
      } ${professional.addressCity} - ${professional.addressState}`
    : null;

  const leftColor = isActive ? "var(--primary)" : "var(--gray)";

  return (
    <>
      <C.Card
        style={{ "--left-color": leftColor } as React.CSSProperties}
        aria-label={`Profissional: ${professionalName}`}
      >
        {/* ── Header ── */}
        <C.CardHeader>
          <C.AvatarCircle aria-hidden="true">
            {getInitials(professional.name)}
          </C.AvatarCircle>

          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{professionalName}</Tooltip>}
          >
            <C.ProfName>{professionalName}</C.ProfName>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                Contrato {ContractStatusDescription[professional.contractStatus]}
              </Tooltip>
            }
          >
            <C.ContractBadge
              $status={professional.contractStatus}
              aria-label={`Contrato ${ContractStatusDescription[professional.contractStatus]}`}
            >
              <i
                className={`uil ${
                  CONTRACT_ICON[professional.contractStatus] ??
                  "uil-file-question-alt"
                }`}
                aria-hidden="true"
              />
              {ContractStatusDescription[professional.contractStatus]}
            </C.ContractBadge>
          </OverlayTrigger>

          <Dropdown align="end">
            <Dropdown.Toggle as="div" bsPrefix="no-caret">
              <C.ActionButton
                aria-label={`Ações para ${professionalName}`}
                aria-haspopup="true"
              >
                <i className="uil uil-ellipsis-v" aria-hidden="true" />
              </C.ActionButton>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item
                as="button"
                onClick={() => setAlertModalShow(true)}
                className={isActive ? "text-danger" : "text-success"}
              >
                <i
                  className={`uil uil-${
                    isActive ? "times" : "check"
                  }-circle me-2`}
                  aria-hidden="true"
                />
                {isActive ? "Desativar" : "Ativar"}
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                onClick={() => setProfessionalEditModalShow(true)}
              >
                <i className="uil uil-edit-alt me-2" aria-hidden="true" />
                Editar
              </Dropdown.Item>

              {professional.contractStatus === ContractStatus.Signed && (  // eslint-disable-line
                <Dropdown.Item
                  as="button"
                  onClick={() =>
                    showProfessionalEmbeddedContractModal(professional.userId)
                  }
                >
                  <i className="uil uil-file-alt me-2" aria-hidden="true" />
                  Ver contrato
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </C.CardHeader>

        {/* ── Body ── */}
        <C.CardBody aria-label="Dados do profissional">
          <C.MetaItem>
            <C.MetaLabel>CPF / CNPJ</C.MetaLabel>
            <C.MetaValue>{cpfAndCnpjMask(professional.cpf)}</C.MetaValue>
          </C.MetaItem>

          <C.MetaItem>
            <C.MetaLabel>Telefone</C.MetaLabel>
            <C.MetaValue>{phone || "—"}</C.MetaValue>
          </C.MetaItem>

          <C.MetaItem>
            <C.MetaLabel>E-mail</C.MetaLabel>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{professional.email}</Tooltip>}
            >
              <C.MetaValue>{professional.email}</C.MetaValue>
            </OverlayTrigger>
          </C.MetaItem>

          <C.MetaItem>
            <C.MetaLabel>Endereço</C.MetaLabel>
            {address ? (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>{address}</Tooltip>}
              >
                <C.MetaValue>{address}</C.MetaValue>
              </OverlayTrigger>
            ) : (
              <C.MetaValueMuted>Não informado</C.MetaValueMuted>
            )}
          </C.MetaItem>
        </C.CardBody>
      </C.Card>

      <AlertModal
        show={alertModalShow}
        onHide={() => setAlertModalShow(false)}
        title={`Tem certeza que deseja ${
          isActive ? "des" : ""
        }ativar esse profissional?`}
        onClickButton={handleActivateProfessional}
        btnVariant={isActive ? "danger" : "success"}
        isLoading={isLoading}
      />

      <ModalComponent
        title="Editar Profissional"
        show={professionalEditModalShow}
        onHide={() => setProfessionalEditModalShow(false)}
        size="xl"
      >
        <ProfessionalEditForm
          onHide={() => setProfessionalEditModalShow(false)}
          professional={professional}
          companyId={companyId}
        />
      </ModalComponent>
    </>
  );
};

export default ProfessionalCard;
